import type {
  CanvasSize,
  CustomEasingCurve,
  CustomSvgData,
  FontSettings,
  LetterOffset,
  ModuleShape,
  DotOffset,
  RendererType,
  RGBColor,
  WordOffset,
} from '../../types/index.js'
import { migrateProjectDocument } from '../project/migrateProjectDocument.js'
import type {
  AnimationSegment,
  AnimationType,
  EasingType,
  LoopMode,
  PlaybackDirection,
  ProjectDocumentV2,
} from '../project/ProjectDocument.js'

/**
 * Complete persisted subset understood by the temporary React/V1 facade.
 * Keeping this conversion explicit makes the compatibility bridge auditable
 * and lets a hydrated V2 document round-trip without defaulting hidden fields.
 */
export interface LegacyProjectFacade {
  readonly seed: number
  readonly text: string
  readonly shape: ModuleShape
  readonly density: number
  readonly color: RGBColor
  readonly chaosColor: RGBColor
  readonly fadeInChaos: boolean
  readonly gridMode: boolean
  readonly colorTransitionStart: number
  readonly colorTransitionEnd: number
  readonly showBackground: boolean
  readonly backgroundColor: RGBColor
  readonly showBorder: boolean
  readonly borderIntensity: number
  readonly borderColor: RGBColor
  readonly borderRadius: number
  readonly borderOnTop: boolean
  readonly borderScale: number
  readonly showGlow: boolean
  readonly glowIntensity: number
  readonly glowColor: RGBColor
  readonly glowSoftness: number
  readonly particleScale: number
  readonly wiggleEnabled: boolean
  readonly wiggleSpeed: number
  readonly wiggleForce: number
  readonly wiggleDampening: number
  readonly rotationEnabled: boolean
  readonly rotationSpeed: number
  readonly motionBlur: number
  readonly fontSettings: FontSettings
  readonly activeFont: string
  readonly animationType: AnimationType
  /** Optional segmented timeline (shared contract); empty = single mode. */
  readonly segments: ReadonlyArray<AnimationSegment>
  readonly animationSpeed: number
  readonly easingType: EasingType
  readonly easingAmount: number
  readonly playbackDirection: PlaybackDirection
  readonly holdAtOrder: number
  readonly holdAtChaos: number
  readonly loopMode: LoopMode
  readonly canvasSize: CanvasSize
  readonly customSvgData: CustomSvgData | null
  readonly customEasingCurve: CustomEasingCurve
  readonly wordOffsets: WordOffset[]
  readonly letterOffsets: LetterOffset[]
  readonly dotOffsets: DotOffset[]
  readonly letterMode: boolean
  readonly dotMode: boolean
  readonly rendererType: RendererType
}

export function legacyFacadeFromProject(input: ProjectDocumentV2): LegacyProjectFacade {
  const project = migrateProjectDocument(input)
  return {
    seed: project.seed,
    text: project.text.content,
    shape: project.particles.shape,
    density: project.particles.density,
    color: project.appearance.color,
    chaosColor: project.appearance.chaosColor,
    fadeInChaos: project.appearance.fadeInChaos,
    gridMode: project.appearance.gridMode,
    colorTransitionStart: project.appearance.colorTransitionStart,
    colorTransitionEnd: project.appearance.colorTransitionEnd,
    showBackground: project.appearance.showBackground,
    backgroundColor: project.appearance.backgroundColor,
    showBorder: project.appearance.showBorder,
    borderIntensity: project.appearance.borderIntensity,
    borderColor: project.appearance.borderColor,
    borderRadius: project.appearance.borderRadius,
    borderOnTop: project.appearance.borderOnTop,
    borderScale: project.appearance.borderScale,
    showGlow: project.appearance.showGlow,
    glowIntensity: project.appearance.glowIntensity,
    glowColor: project.appearance.glowColor,
    glowSoftness: project.appearance.glowSoftness,
    particleScale: project.particles.scale,
    wiggleEnabled: project.animation.wiggleEnabled,
    wiggleSpeed: project.animation.wiggleSpeed,
    wiggleForce: project.animation.wiggleForce,
    wiggleDampening: project.animation.wiggleDampening,
    rotationEnabled: project.animation.rotationEnabled,
    rotationSpeed: project.animation.rotationSpeed,
    motionBlur: project.animation.motionBlur,
    fontSettings: {
      fontSize: project.text.fontSize,
      lineHeight: project.text.lineHeight,
      wordSpacing: project.text.wordSpacing,
      textAlign: project.text.textAlign,
      baseline: project.text.baseline,
    },
    activeFont: project.text.activeFont,
    animationType: project.animation.type,
    segments: project.animation.segments ?? [],
    animationSpeed: project.animation.speed,
    easingType: project.animation.easingType,
    easingAmount: project.animation.easingAmount,
    playbackDirection: project.animation.playbackDirection,
    holdAtOrder: project.animation.holdAtOrder,
    holdAtChaos: project.animation.holdAtChaos,
    loopMode: project.animation.loopMode,
    canvasSize: project.canvas,
    customSvgData: project.particles.customSvg,
    customEasingCurve: project.animation.customEasingCurve,
    wordOffsets: project.layout.wordOffsets,
    letterOffsets: project.layout.letterOffsets,
    dotOffsets: project.layout.dotOffsets,
    letterMode: project.layout.letterMode,
    dotMode: project.layout.dotMode,
    rendererType: project.canvas.preferredRenderer === 'webgl2' ? 'pixi' : 'canvas2d',
  }
}
